import Cookies from 'js-cookie';

export default class UndergraduateEntryRequirements {
  constructor(el) {
    this.el = el;
    this.countryEndpoint = '/feeds/countrypicker/?countries=true';
    this.hasRequirementsEndpoint = '/feeds/apex/hasErsUgProgrammeCountry';
    this.requirementsEndpoint = '/feeds/apex/ersUgProgrammeCountry';
    this.cookieName = 'gu_ct';
    this.cookieDomain = '.gla.ac.uk';
    this.cookieExpires = 365;
    this.countrySelectId = 'ug-country-selector';
    this.courseSelectId = 'ug-course-selector';
    this.countrySelectClass = 'entry-country-select';
    this.countries = [];
    this.courseOptions = [];
    this.init();
  }

  async init() {
    this.insertControls();
    await Promise.all([this.loadCountries(), this.loadCourses()]);

    this.syncCountrySelection();
    this.syncCourseSelection();

    this.countrySelector.addEventListener('change', () => {
      this.setCountryCookie(this.countrySelector.value);
      this.renderRequirements();
    });

    this.courseSelector.addEventListener('change', () => {
      this.renderRequirements();
    });

    this.renderRequirements();
  }

  insertControls() {
    this.controls = document.createElement('div');
    this.controls.className = 'ug-entry-requirements-controls';
    this.controls.innerHTML = `
      <label class="visuallyhidden ug-entry-requirements-label" for="${this.courseSelectId}">Select your degree or UCAS code</label>
      <select id="${this.courseSelectId}" class="ug-course-select" aria-label="Select your degree or UCAS code"><option value="">Select your degree or UCAS code</option></select>
      <label class="visuallyhidden ug-entry-requirements-label" for="${this.countrySelectId}">Country/region</label>
      <select id="${this.countrySelectId}" class="${this.countrySelectClass}" aria-label="Select your country/region"></select>
   `;
    this.el.parentNode.insertBefore(this.controls, this.el);
    this.countrySelector = this.controls.querySelector(
      `#${this.countrySelectId}`
    );
    this.courseSelector = this.controls.querySelector(
      `#${this.courseSelectId}`
    );
  }

  async loadCountries() {
    const response = await fetch(this.countryEndpoint);
    this.countries = await response.json();

    this.countrySelector.innerHTML = `<option value="">Select your country/region</option>${this.countries
      .map(
        (country) =>
          `<option data-id="${country.id}" value="${country.id}">${this.escapeHtml(country.title)}</option>`
      )
      .join('')}`;
  }

  loadCourses() {
    const source = globalThis.combinationInfo || window.combinationInfo || [];

    const seen = new Set();
    this.courseOptions = [];

    source.forEach((group) => {
      (group.combinations || []).forEach((combination) => {
        if (!combination?.ucasCode || seen.has(combination.ucasCode)) {
          return;
        }

        seen.add(combination.ucasCode);
        this.courseOptions.push({
          value: combination.ucasCode,
          label: this.buildCourseLabel(group, combination),
        });
      });
    });

    // Build options safely using DOM methods to avoid double-encoding HTML entities
    // and to ensure any literal "&amp;" in the source is displayed as "&" in the dropdown.
    this.courseSelector.innerHTML = '';
    const fragment = document.createDocumentFragment();

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Select your degree or UCAS code';
    fragment.appendChild(defaultOption);

    this.courseOptions.forEach((course) => {
      const opt = document.createElement('option');
      // Use the raw values for the option value (they should already be safe for form submission),
      // and textContent for the label so the browser renders text literally (no HTML parsing).
      // If the source contains an HTML-encoded ampersand (&amp;) we normalise it to a plain ampersand.
      opt.value =
        typeof course.value === 'string' ? course.value : String(course.value);
      opt.textContent =
        typeof course.label === 'string'
          ? course.label.replace(/&amp;/g, '&')
          : String(course.label);
      fragment.appendChild(opt);
    });

    this.courseSelector.appendChild(fragment);
  }

  buildCourseLabel(group, combination) {
    const parts = [];

    if (combination.combinationName) {
      parts.push(combination.combinationName);
    }

    if (group?.designation) {
      parts.push(group.designation);
    }

    if (combination?.ucasCode) {
      parts.push(combination.ucasCode);
    }

    return parts.join(' - ');
  }

  syncCountrySelection() {
    const storedCountry = this.getCountryCookie();
    const validCountry = this.countries.find(
      (country) => String(country.id) === String(storedCountry)
    );
    const defaultCountry = validCountry || this.getDefaultUKCountry();

    if (defaultCountry) {
      this.countrySelector.value = String(defaultCountry.id);
      if (!storedCountry || !validCountry) {
        this.setCountryCookie(defaultCountry.id);
      }
    }
  }

  syncCourseSelection() {
    if (this.courseSelector.options.length > 0) {
      this.courseSelector.selectedIndex = 0;
    }
  }

  getDefaultUKCountry() {
    return (
      this.countries.find((country) => country.isUk === true) ||
      this.countries[0]
    );
  }

  getCountryCookie() {
    return Cookies.get(this.cookieName) || false;
  }

  setCountryCookie(value) {
    if (!value) {
      return;
    }

    const options = {
      expires: this.cookieExpires,
      secure: window.location.protocol === 'https:',
      path: '/',
    };

    if (window.location.hostname.endsWith('.gla.ac.uk')) {
      options.domain = this.cookieDomain;
    }

    Cookies.set(this.cookieName, value, options);
  }

  async renderRequirements() {
    if (!this.countrySelector.value || !this.courseSelector.value) {
      this.el.innerHTML = '';
      return;
    }

    this.el.innerHTML = `
      <div style="text-align: center;">
        <svg xmlns="http://www.w3.org/2000/svg" width="200px" height="200px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" class="lds-ellipsis" style="background: none; text-align: center;"><circle cx="84" cy="50" r="0" fill="#5b4d94"><animate attributeName="r" values="10;0;0;0;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate><animate attributeName="cx" values="84;84;84;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate></circle><circle cx="16" cy="50" r="9.6148" fill="#951272"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="-1.85s"></animate><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="-1.85s"></animate></circle><circle cx="84" cy="50" r="0.385197" fill="#0274af"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="-0.925s"></animate><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="-0.925s"></animate></circle><circle cx="82.6903" cy="50" r="10" fill="#011451"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate></circle><circle cx="48.6903" cy="50" r="10" fill="#5b4d94"><animate attributeName="r" values="0;0;10;10;10" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate><animate attributeName="cx" values="16;16;16;50;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="3.7s" repeatCount="indefinite" begin="0s"></animate></circle></svg>
      </div>`;

    const countryCode = this.countrySelector.value;
    const courseCode = this.courseSelector.value;

    try {
      const hasResponse = await fetch(
        `${this.hasRequirementsEndpoint}/${countryCode}/${courseCode}`
      );
      const hasData = await hasResponse.json();

      if (!hasData?.hasRequirements) {
        this.el.innerHTML =
          '<p>No undergraduate entry requirements are available for the selected country and course.</p>';
        return;
      }

      const response = await fetch(
        `${this.requirementsEndpoint}/${countryCode}/${courseCode}`
      );
      const data = await response.json();
      this.el.innerHTML = this.renderRequirementBlocks(data);
    } catch (error) {
      console.error('Error loading undergraduate entry requirements:', error);
      this.el.innerHTML =
        '<p>Unable to load undergraduate entry requirements at this time.</p>';
    }
  }

  renderRequirementBlocks(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return '<p>No undergraduate entry requirements are available for the selected course.</p>';
    }

    const blocks = data
      .map((item) => this.renderRequirementBlock(item))
      .filter(Boolean);

    return blocks.length > 0
      ? blocks.join('')
      : '<p>No undergraduate entry requirements are available for the selected course.</p>';
  }

  renderRequirementBlock(item) {
    if (!item) {
      return '';
    }

    const title = this.cleanText(item.qualification_title);
    const body = this.renderQualificationText(item.qualification_text);

    if (!title && !body) {
      return '';
    }

    return `
      <section class="ug-entry-requirement">
        ${title ? `<h3>${this.escapeHtml(title)}</h3>` : ''}
        ${body}
      </section>
    `;
  }

  renderQualificationText(text) {
    const cleaned = this.cleanText(text);

    if (!cleaned) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = cleaned;

    container.querySelectorAll('h2').forEach((heading) => {
      const h4 = document.createElement('h4');
      h4.innerHTML = heading.innerHTML;

      [...heading.attributes].forEach((attribute) => {
        h4.setAttribute(attribute.name, attribute.value);
      });

      heading.replaceWith(h4);
    });

    container.querySelectorAll('li').forEach((item) => {
      if (!this.cleanText(item.textContent)) {
        item.remove();
      }
    });

    container.querySelectorAll('ul, ol').forEach((list) => {
      if (!list.querySelector('li')) {
        list.remove();
      }
    });

    return this.cleanText(container.innerHTML);
  }

  cleanText(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  escapeHtml(value) {
    return this.cleanText(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
